import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AdminPagination } from '@/components/AdminPagination';
import { AdminPill } from '@/components/AdminPill';
import { AdminTableSkeletonRows } from '@/components/AdminTableSkeletonRows';
import { AdminTableToolbar } from '@/components/AdminTableToolbar';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminTablePagination } from '@/hooks/useAdminTablePagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  isExpirationPast,
  publishDeal,
  republishDeal,
  softDeleteDeal,
  transitionDealStatus,
} from '@/lib/db/admin/deals';
import {
  PROPERTY_TYPES,
  type AdminDealRow,
  type AdminDealStatus,
  type PropertyType,
} from '@/lib/db/deals';
import { formatGstDate } from '@/lib/format';
import { parsePageParam, parsePageSizeParam } from '@/lib/pagination';
import { getPgError } from '@/lib/pg-error';
import { getSupabaseClient } from '@/lib/supabase';
import { useAdminDeals } from './AdminDealsPage.data';

type BulkAction = 'submitForValidation' | 'publish' | 'archive' | 'delete';

const FILTERS = ['all', 'draft', 'validating', 'published', 'refused', 'sold', 'archived'] as const;
type FilterKey = (typeof FILTERS)[number];

const STATUS_PILL_KEY: Record<string, string> = {
  draft: 'admin.common.draftPill',
  validating: 'admin.common.validatingPill',
  published: 'admin.common.publishedPill',
  refused: 'admin.common.refusedPill',
  archived: 'admin.common.archivedPill',
  sold: 'admin.common.soldPill',
  expired: 'admin.common.archivedPill',
};

const STATUS_PILL_VARIANT: Record<
  string,
  'PUBLISHED' | 'DRAFT' | 'VALIDATING' | 'REFUSED' | 'ARCHIVED' | 'SOLD'
> = {
  draft: 'DRAFT',
  validating: 'VALIDATING',
  published: 'PUBLISHED',
  refused: 'REFUSED',
  archived: 'ARCHIVED',
  sold: 'SOLD',
  expired: 'ARCHIVED',
};

const PRICE_FORMATTER = new Intl.NumberFormat('en-AE', {
  style: 'currency',
  currency: 'AED',
  maximumFractionDigits: 0,
});

function filterToStatus(filter: FilterKey): AdminDealStatus | 'all' {
  if (filter === 'all') return 'all';
  return filter as AdminDealStatus;
}

// FSM eligibility per Modele V1.2 §5.1 / deals_check_status_transition:
// only validating→published and published→archived are legal manual
// transitions. Without this filter, a mixed selection (e.g. drafts +
// validating clicked through "Publish") would surface six PostgREST
// 400s with check_violation 23514 and a useless "N failed" toast.
function isEligibleForBulk(action: BulkAction, status: AdminDealStatus): boolean {
  if (action === 'submitForValidation') return status === 'draft';
  if (action === 'publish') return status === 'validating';
  if (action === 'archive') return status === 'published';
  return true;
}

export function AdminDealsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const isSuperAdmin = auth.status === 'authenticated' && auth.profile.role === 'super_admin';
  const [filter, setFilter] = useState<FilterKey>('all');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState<PropertyType[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmBulk, setConfirmBulk] = useState<BulkAction | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // D3 republish is per-row (not bulk): the confirm modal names the listing,
  // so it holds the row rather than a boolean.
  const [confirmRepublish, setConfirmRepublish] = useState<AdminDealRow | null>(null);
  const [republishSubmitting, setRepublishSubmitting] = useState(false);

  const togglePropertyTypeFilter = (pt: PropertyType) => {
    setPropertyTypeFilter((prev) =>
      prev.includes(pt) ? prev.filter((x) => x !== pt) : [...prev, pt],
    );
  };

  // URL is the pagination source of truth (read every render — Back/Forward
  // replays states natively). Writes go through useAdminTablePagination.
  const [searchParams] = useSearchParams();
  const page = parsePageParam(searchParams.get('page'));
  const pageSize = parsePageSizeParam(searchParams.get('pageSize'));
  const debouncedSearch = useDebouncedValue(searchQuery);
  const propertyTypeKey = propertyTypeFilter.join(',');

  const {
    rows,
    total,
    status: queryStatus,
    refetch,
  } = useAdminDeals({
    status: filterToStatus(filter),
    searchQuery: debouncedSearch,
    propertyType: propertyTypeFilter,
    page,
    pageSize,
  });

  const { goPage, setPageSize } = useAdminTablePagination({
    page,
    pageSize,
    total,
    isSuccess: queryStatus === 'success',
    filterKey: `${filter}|${debouncedSearch}|${propertyTypeKey}`,
  });

  // The bulk selection is page-scoped: rows that left the viewport must not
  // stay silently selected (an archive could otherwise hit invisible deals).
  useEffect(() => {
    setSelected(new Set());
  }, [page, pageSize, filter, debouncedSearch, propertyTypeKey]);

  const toggleAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(new Set(rows.map((deal) => deal.id)));
    } else {
      setSelected(new Set());
    }
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (action: BulkAction) => {
    const ids = Array.from(selected);
    const rowById = new Map(rows.map((row) => [row.id, row]));
    let eligibleIds = ids.filter((id) => {
      const row = rowById.get(id);
      return row ? isEligibleForBulk(action, row.status) : false;
    });
    const skippedCount = ids.length - eligibleIds.length;
    let skippedNoPhotosCount = 0;

    // Publish carries a second DB precondition beyond status:
    // deals_check_status_transition §4.2 rejects with 23514 if a deal
    // about to land on 'published' has zero deal_photos. Pre-check in a
    // single batched query so the toast reads "X need a photo" instead
    // of "X failed".
    if (action === 'publish' && eligibleIds.length > 0) {
      try {
        const client = getSupabaseClient();
        const { data: photoRows, error: photosErr } = await client
          .from('deal_photos')
          .select('deal_id')
          .in('deal_id', eligibleIds);
        if (!photosErr && photoRows) {
          const haveAtLeastOnePhoto = new Set(
            (photoRows as { deal_id: string }[]).map((p) => p.deal_id),
          );
          const beforePhotoFilter = eligibleIds.length;
          eligibleIds = eligibleIds.filter((id) => haveAtLeastOnePhoto.has(id));
          skippedNoPhotosCount = beforePhotoFilter - eligibleIds.length;
        }
      } catch {
        // Pre-check failed (network / RLS surprise). Fall through and
        // let the FSM enforce; user gets the generic partialError toast
        // instead of the friendlier "needs photos" one.
      }
    }

    if (eligibleIds.length === 0) {
      if (action === 'publish' && skippedNoPhotosCount > 0) {
        toast.error(
          t('admin.deals.bulk.noEligibleToast.publishNoPhotos', {
            count: skippedNoPhotosCount,
          }),
        );
      } else {
        toast.error(t(`admin.deals.bulk.noEligibleToast.${action}`, { count: ids.length }));
      }
      setConfirmBulk(null);
      return;
    }

    setBulkSubmitting(true);
    try {
      // Promise.allSettled per id so a single failure (RLS, precondition,
      // race) doesn't sink the rest of the batch. The UI toasts a success
      // count + an error count when both happen.
      // Explicit Promise<unknown> return type so the ternary unifies the
      // four differently-typed callbacks (softDeleteDeal returns void,
      // the others return AdminDeal) into a single signature .map can
      // dispatch on.
      const operation: (id: string) => Promise<unknown> =
        action === 'delete'
          ? (id) => softDeleteDeal(id)
          : action === 'publish'
            ? (id) => publishDeal(id)
            : action === 'submitForValidation'
              ? (id) => transitionDealStatus({ dealId: id, toStatus: 'validating' })
              : (id) => transitionDealStatus({ dealId: id, toStatus: 'archived' });
      const results = await Promise.allSettled(eligibleIds.map(operation));
      const okCount = results.filter((r) => r.status === 'fulfilled').length;
      const failCount = results.length - okCount;

      const successKey =
        action === 'delete'
          ? 'admin.deals.bulk.deleteToast'
          : action === 'publish'
            ? 'admin.deals.bulk.publishToast'
            : action === 'submitForValidation'
              ? 'admin.deals.bulk.submitForValidationToast'
              : 'admin.deals.bulk.archiveToast';

      if (okCount > 0) toast.success(t(successKey, { count: okCount }));
      if (skippedCount > 0 && action !== 'delete') {
        toast.info(t(`admin.deals.bulk.skippedToast.${action}`, { count: skippedCount }));
      }
      if (skippedNoPhotosCount > 0) {
        toast.info(
          t('admin.deals.bulk.skippedToast.publishNoPhotos', { count: skippedNoPhotosCount }),
        );
      }
      if (failCount > 0) {
        toast.error(t('admin.deals.bulk.partialError', { count: failCount }));
      }
      refetch();
      setSelected(new Set());
      setConfirmBulk(null);
    } catch {
      toast.error(t('admin.dealEdit.errorTransitionToast'));
    } finally {
      setBulkSubmitting(false);
    }
  };

  // D3 (Modele V1.2 §10.1) — republish an archived listing in place. Goes
  // through the `republish_deal` RPC, which locks the row and refuses
  // anything that is no longer exactly `archived`, so a double click or a
  // concurrent admin cannot produce two "republished" toasts.
  const runRepublish = async (row: AdminDealRow) => {
    setRepublishSubmitting(true);
    try {
      const restored = await republishDeal(row.id);
      // A past expiration_date does not block the republication, but the
      // nightly auto_expire_deals CRON undoes it — warn instead of reporting
      // a plain success (ADR-059).
      if (isExpirationPast(restored.expiration_date)) {
        toast.warning(t('admin.dealEdit.transitions.republishedExpiredToast'));
      } else {
        toast.success(t('admin.dealEdit.transitions.republishedToast'));
      }
      setConfirmRepublish(null);
      refetch();
    } catch (err) {
      const pg = getPgError(err);
      if (pg.code === '55000' && pg.message.toLowerCase().includes('soft-deleted')) {
        toast.error(t('admin.dealEdit.errorRepublishDeletedToast'));
      } else if (pg.code === '55000') {
        toast.error(t('admin.dealEdit.errorRepublishNotArchivedToast'));
      } else if (pg.code === '42501') {
        toast.error(t('admin.dealEdit.errorRepublishForbiddenToast'));
      } else {
        toast.error(t('admin.dealEdit.errorTransitionToast'));
      }
      // 55000 means the row moved under us — close and reload so the list
      // stops offering an action this deal no longer supports.
      if (pg.code === '55000') {
        setConfirmRepublish(null);
        refetch();
      }
    } finally {
      setRepublishSubmitting(false);
    }
  };

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">{t('admin.deals.title')}</h1>
        </div>
        <div className="admin-head__actions">
          <button type="button" className="admin-btn" onClick={() => navigate('/admin/deals/new')}>
            {t('admin.deals.newDeal')}
          </button>
          <Link to="/admin/import" className="admin-btn admin-btn--outline">
            {t('admin.deals.importCsv')}
          </Link>
          <button type="button" className="admin-btn admin-btn--outline">
            {t('admin.deals.export')}
          </button>
        </div>
      </header>

      <AdminTableToolbar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('admin.common.searchPlaceholder')}
        secondaryRow={
          <div
            className="admin-filter-tabs"
            role="group"
            aria-label={t('admin.deals.filters.propertyTypeAria')}
          >
            {PROPERTY_TYPES.map((pt) => (
              <button
                key={pt}
                type="button"
                className={`admin-filter-tab${propertyTypeFilter.includes(pt) ? ' is-active' : ''}`}
                aria-pressed={propertyTypeFilter.includes(pt)}
                onClick={() => togglePropertyTypeFilter(pt)}
              >
                {t(`catalogue.filters.propertyTypeOpts.${pt}`)}
              </button>
            ))}
          </div>
        }
      >
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={`admin-filter-tab${filter === key ? ' is-active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {t(`admin.deals.filters.${key}`)}
          </button>
        ))}
      </AdminTableToolbar>

      <div className="admin-table-wrap" aria-busy={queryStatus === 'loading'}>
        <table className="admin-table admin-table--sticky-actions">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={selected.size === rows.length && rows.length > 0}
                  onChange={toggleAll}
                  aria-label="Select all deals"
                />
              </th>
              <th>{t('admin.deals.headers.id')}</th>
              <th>{t('admin.deals.headers.title')}</th>
              <th>{t('admin.deals.headers.type')}</th>
              <th>{t('admin.deals.headers.propertyType')}</th>
              <th>{t('admin.deals.headers.zone')}</th>
              <th className="admin-cell--right">{t('admin.deals.headers.price')}</th>
              <th className="admin-cell--right">{t('admin.deals.headers.discount')}</th>
              <th>{t('admin.deals.headers.status')}</th>
              <th>{t('admin.deals.headers.updated')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {queryStatus === 'loading' ? (
              <AdminTableSkeletonRows columns={11} />
            ) : (
              rows.map((deal) => (
                <tr
                  key={deal.id}
                  className="is-clickable"
                  onClick={() => navigate(`/admin/deals/${deal.id}/edit`)}
                >
                  <td onClick={(event) => event.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(deal.id)}
                      onChange={() => toggleOne(deal.id)}
                      aria-label={`Select ${deal.title}`}
                    />
                  </td>
                  <td className="admin-cell--mono admin-cell--id">
                    {deal.reference_id ?? deal.id.slice(0, 8)}
                  </td>
                  <td className="admin-cell--title">
                    <span className="admin-cell-ellipsis" title={deal.title}>
                      {deal.title}
                      {deal.bedrooms !== null ? ` — ${deal.bedrooms}BR` : ''}
                    </span>
                  </td>
                  <td>{deal.type === 'off_plan' ? 'Off-plan' : 'Ready'}</td>
                  <td>
                    {deal.property_type
                      ? t(`catalogue.filters.propertyTypeOpts.${deal.property_type}`)
                      : '—'}
                  </td>
                  <td>{deal.zone ?? '—'}</td>
                  <td className="admin-cell--mono admin-cell--right">
                    {PRICE_FORMATTER.format(deal.asking_price)}
                  </td>
                  <td className="admin-cell--mono admin-cell--right admin-cell--positive">
                    {deal.discount_pct !== null ? `${deal.discount_pct}%` : '—'}
                  </td>
                  <td>
                    <AdminPill
                      status={STATUS_PILL_VARIANT[deal.status] ?? 'DRAFT'}
                      label={t(STATUS_PILL_KEY[deal.status] ?? 'admin.common.draftPill')}
                    />
                  </td>
                  <td className="admin-cell--mono">{formatGstDate(deal.updated_at)}</td>
                  <td onClick={(event) => event.stopPropagation()}>
                    {/* This cell held a single action until Republish landed;
                        two bare buttons render glued together ("RepublishEdit →").
                        admin-cell-actions is the existing multi-action wrapper
                        (AdminAdvisorsPage) — flex + gap, so it stays correct in RTL. */}
                    <div className="admin-cell-actions">
                      {isSuperAdmin && deal.status === 'archived' ? (
                        <button
                          type="button"
                          className="admin-cell-link"
                          disabled={republishSubmitting}
                          onClick={() => setConfirmRepublish(deal)}
                        >
                          {t('admin.deals.rowActions.republish')}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="admin-cell-link"
                        onClick={() => navigate(`/admin/deals/${deal.id}/edit`)}
                      >
                        {t('admin.deals.headers.edit')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={goPage}
        onPageSizeChange={setPageSize}
        idPrefix="admin-deals"
      />

      {selected.size > 0 ? (
        <div className="admin-bulk-bar">
          <span className="admin-bulk-bar__count">
            {t('admin.deals.bulk.count', { count: selected.size })}
          </span>
          <button
            type="button"
            className="admin-bulk-btn"
            disabled={bulkSubmitting}
            onClick={() => setConfirmBulk('submitForValidation')}
          >
            {t('admin.deals.bulk.submitForValidation')}
          </button>
          {isSuperAdmin ? (
            <button
              type="button"
              className="admin-bulk-btn"
              disabled={bulkSubmitting}
              onClick={() => setConfirmBulk('publish')}
            >
              {t('admin.deals.bulk.publish')}
            </button>
          ) : null}
          <button
            type="button"
            className="admin-bulk-btn admin-bulk-btn--ghost"
            disabled={bulkSubmitting}
            onClick={() => setConfirmBulk('archive')}
          >
            {t('admin.deals.bulk.archive')}
          </button>
          <button
            type="button"
            className="admin-bulk-btn admin-bulk-btn--danger"
            disabled={bulkSubmitting}
            onClick={() => setConfirmBulk('delete')}
          >
            {t('admin.deals.bulk.delete')}
          </button>
          <button type="button" className="admin-bulk-clear" onClick={() => setSelected(new Set())}>
            {t('admin.deals.bulk.clear')}
          </button>
        </div>
      ) : null}

      {confirmBulk ? (
        <ConfirmModal
          title={t(`admin.deals.bulk.confirm.${confirmBulk}.title`, { count: selected.size })}
          body={t(`admin.deals.bulk.confirm.${confirmBulk}.body`, { count: selected.size })}
          danger={confirmBulk === 'delete'}
          requireType={
            confirmBulk === 'delete' ? t('admin.deals.bulk.confirm.delete.requireType') : undefined
          }
          onConfirm={() => {
            if (!bulkSubmitting) void runBulk(confirmBulk);
          }}
          onCancel={() => setConfirmBulk(null)}
        />
      ) : null}

      {confirmRepublish ? (
        <ConfirmModal
          title={t('admin.deals.confirmRepublish.title')}
          body={
            <span>
              <strong>{confirmRepublish.reference_id ?? confirmRepublish.id.slice(0, 8)}</strong> —{' '}
              {confirmRepublish.title}
              <br />
              {t('admin.deals.confirmRepublish.body')}
              {isExpirationPast(confirmRepublish.expiration_date) ? (
                <strong style={{ display: 'block', marginTop: 10, color: 'var(--ddm-warning)' }}>
                  {t('admin.deals.confirmRepublish.expiredWarning', {
                    date: formatGstDate(confirmRepublish.expiration_date),
                  })}
                </strong>
              ) : null}
            </span>
          }
          confirmLabel={t('admin.deals.rowActions.republish')}
          busy={republishSubmitting}
          onConfirm={() => {
            if (!republishSubmitting) void runRepublish(confirmRepublish);
          }}
          onCancel={() => setConfirmRepublish(null)}
        />
      ) : null}
    </>
  );
}
